import React from "react";
import { twMerge } from "tailwind-merge";

// Define style variants for the container and the pill
const VARIANT_STYLES = {
  info: {
    container: "border-primary-500",
    pill: "bg-primary-500 border-primary-500 dark:border-secondary-500 text-secondary-700 dark:text-secondary-100",
  },
  success: {
    container: "border-accent_two-500",
    pill: "bg-accent_two-500 border-accent_two-500 dark:border-accent_two-500 text-accent_two-50 dark:text-accent_two-300",
  },
  warning: {
    container: "border-accent_one-600",
    pill: "bg-accent_one-600 border-accent_one-600 dark:border-accent_one-500 text-accent_one-50 dark:text-accent_one-100",
  },
  error: {
    container: "border-accent_three-700",
    pill: "bg-accent_three-700 border-accent_three-700 dark:border-accent_three-700 text-accent_three-50 dark:text-accent_three-100",
  },
};

interface MessageProps {
  variant: keyof typeof VARIANT_STYLES;
  pillMessage: string;
  message: React.ReactNode;
  className?: string;
}

/**
 * StatusMessage component to display a message with a pill and a message.
 * @param {string} variant - The variant of the message (info, success, warning, error).
 * @param {string} pillMessage - The text to display in the pill.
 * @param {React.ReactNode} message - The main message content.
 * @param {string} className - Additional classes for styling.
 */
export const StatusMessage: React.FC<MessageProps> = ({
  variant,
  pillMessage,
  message,
  className,
}) => {
  return (
    <div
      className={twMerge(
        "flex items-center space-x-3 rounded-full border bg-white/10 p-4",
        VARIANT_STYLES[variant].container,
        className
      )}
    >
      <span
        className={twMerge(
          "rounded-full border px-3 py-2 text-sm font-semibold dark:bg-white/0",
          VARIANT_STYLES[variant].pill
        )}
      >
        {pillMessage}
      </span>
      <span className="text-sm leading-3 text-pretty">{message}</span>
    </div>
  );
};
